import getObjectKey from 'lib/object/getObjectKey'
import { getJourneyV2AveragePricePerTraveller } from 'lib/flights/flightUtils'
import { FlightViewTypes } from 'constants/flight'
import { AllOrderItemTypes, getAllOrderItems, isItemCancelled } from 'lib/order/orderUtils'
import { addDays, beginningOfToday, formatIsoDate } from 'lib/datetime/dateUtils'

/**
 * Utility functions for flight date change flow
 */

export const getFlightDateChangeUpsellPricePerLeg = (
  departingJourney: App.JourneyV2,
  returningJourney: App.JourneyV2 | undefined,
  selectedLegCount: number,
): number => {
  const totalJourneyPricePerTraveller = getJourneyV2AveragePricePerTraveller(departingJourney) +
    (returningJourney ? getJourneyV2AveragePricePerTraveller(returningJourney) : 0)

  return totalJourneyPricePerTraveller / selectedLegCount
}

export const getFlightDateChangeSearchKey = (
  reservationId: string,
  selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>,
): string => getObjectKey({
  reservationId,
  selectedLegs: selectedLegs.map(leg => leg.id).join(','),
  dates: selectedLegs.map(leg => leg.newDepartureDate).join(','),
})

/**
 * Creates a combined fare key for upsell lookup
 * The key format is 'journeyId1,journeyId2,...' matching all legs in order
 */
export const createCombinedFareKey = (
  selectedJourneys: Record<string, App.JourneyV2>,
  allLegs: Array<App.LegWithFlightItemId>,
): string => {
  return allLegs
    .map(leg => selectedJourneys[leg.id]?.id)
    .filter(Boolean)
    .join(',')
}

/**
 * Checks if upsell data exists for a journey combination
 */
export const hasUpsellDataForCombination = (
  fareKey: string,
  fareIdsToUpsellFarePerLeg: Record<string, App.FlightDateChangeUpsellFaresResult>,
): boolean => {
  return !!fareIdsToUpsellFarePerLeg[fareKey]?.upsellPerLeg?.length
}

/**
 * Gets the complete journey selection including user selections and auto-selected unchanged legs
 * For unchanged legs, respects pairedFareId relationships using bookingInfo.boookingKey
 */
export const getCompleteJourneySelection = (
  userSelectedJourneys: Record<string, App.JourneyV2>,
  allAvailableJourneys: Array<Array<App.JourneyV2>>,
  selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>,
  allLegs: Array<App.LegWithFlightItemId>,
): Record<string, App.JourneyV2> => {
  const result: Record<string, App.JourneyV2> = { ...userSelectedJourneys }

  // Build a map of journeys by bookingKey for each leg (used for pairing)
  const journeysByBookingKeyPerLeg = allAvailableJourneys.map(journeys => {
    const map = new Map<string, App.JourneyV2>()
    journeys.forEach(journey => {
      if (journey.bookingInfo?.boookingKey) {
        map.set(journey.bookingInfo.boookingKey, journey)
      }
    })
    return map
  })

  // Auto-select journeys for unchanged legs, respecting pairedFareId
  allLegs.forEach((leg, legIndex) => {
    const isSelectedForChange = selectedLegs.some(selectedLeg => selectedLeg.id === leg.id)

    // If this leg is not being changed and not already selected
    if (!isSelectedForChange && !result[leg.id] && allAvailableJourneys[legIndex]?.length > 0) {
      let foundPairedJourney: App.JourneyV2 | undefined

      // Check all user-selected journeys for pairing information
      for (const selectedJourney of Object.values(userSelectedJourneys)) {
        if (selectedJourney.pairedFareId?.length) {
          // Find a journey in this leg whose bookingKey matches the paired list
          for (const pairedBookingKey of selectedJourney.pairedFareId) {
            const pairedJourney = journeysByBookingKeyPerLeg[legIndex].get(pairedBookingKey)
            if (pairedJourney) {
              foundPairedJourney = pairedJourney
              break
            }
          }
        }
        if (foundPairedJourney) break
      }

      // Use paired journey if found, otherwise fall back to first available
      result[leg.id] = foundPairedJourney || allAvailableJourneys[legIndex][0]
    }
  })

  return result
}

/**
 * Determines the current page location in the flight date change flow based on URL pathname.
 * Used for reliable navigation since Redux state may not always match the actual page.
 */
export function getFlightChangeDatesPageLocation(pathname: string) {
  return {
    isOnFlightChangePage: pathname.includes('flight-change-select-flight-to-change'),
    isOnPurchasePage: pathname.includes('/purchase'),
  }
}

/**
 * Days of notice flights search needs — it returns nothing for today or tomorrow, which is why the
 * date-change calendar disables both.
 */
export const FLIGHT_CHANGE_SEARCH_LEAD_DAYS = 2

/** The earliest date flights search will return an exchange for. */
export function getEarliestFlightChangeDepartureDate(): string {
  return formatIsoDate(addDays(beginningOfToday(), FLIGHT_CHANGE_SEARCH_LEAD_DAYS))
}

/** The dates before {@link getEarliestFlightChangeDepartureDate}, for the calendar to disable. */
export function getFlightChangeUnavailableDepartureDates(): Array<string> {
  const today = beginningOfToday()
  return Array.from({ length: FLIGHT_CHANGE_SEARCH_LEAD_DAYS }, (_unused, offset) =>
    formatIsoDate(addDays(today, offset)),
  )
}

/**
 * Whether the order holds nothing but flights, for the purposes of a flight date change.
 *
 * Travel protection is deliberately not counted — it is an attachment to the flight, not a product
 * that blocks moving it. This is why it does not reuse `isStandaloneFlightsOrder`, which counts
 * insurance.
 *
 * Derived from `getAllOrderItems` rather than a hand-listed set of item arrays so a newly added item
 * type can't silently pass this gate.
 */
export function isFlightOnlyOrderForDateChange(order: App.Order): boolean {
  if (order.flightItems.length === 0) return false

  // By identity, not a type guard: getAllOrderItems spreads these same object references.
  const nonBlockingItems = new Set<AllOrderItemTypes>([
    ...order.flightItems,
    ...order.bookingProtectionItems,
  ])

  const hasOtherActiveItems = getAllOrderItems(order).some(
    item => !nonBlockingItems.has(item) && !isItemCancelled(item),
  )

  // Gift cards sit outside getAllOrderItems and carry no status.
  return !hasOtherActiveItems && order.giftCardItems.length === 0
}

export type FlightDateChangeIneligibleReason =
  | 'flag-disabled'
  | 'item-not-completed'
  | 'not-flight-only'
  | 'no-edit-access'
  | 'details-unavailable'
  | 'fare-not-changeable'
  | 'mixed-providers'
  | 'reservation-locked'

/**
 * The single gate for entering the flight date change flow, so every entry point applies the same
 * rules. Reasons are for logging, not customer-facing copy.
 */
export function getFlightDateChangeIneligibleReason({
  order,
  flightItem,
  flightDetails,
  isFlagEnabled,
  canEdit,
}: {
  order: App.Order
  flightItem: App.OrderFlightItem | undefined
  flightDetails: App.FlightOrderItemDetails | undefined
  isFlagEnabled: boolean
  canEdit: boolean
}): FlightDateChangeIneligibleReason | undefined {
  if (!isFlagEnabled) return 'flag-disabled'
  if (!flightItem || flightItem.status !== 'completed') return 'item-not-completed'
  if (!canEdit) return 'no-edit-access'
  if (!isFlightOnlyOrderForDateChange(order)) return 'not-flight-only'
  if (!flightDetails) return 'details-unavailable'
  if (order.locked || flightDetails.metadata?.lockedAt) return 'reservation-locked'
  // A mix-and-match pair of one-ways is stored as one reservation per leg, so changing one would
  // leave the other stranded.
  if (
    flightDetails.viewType === FlightViewTypes.TWO_ONE_WAYS &&
    flightDetails.tripType === FlightViewTypes.ONE_WAY
  ) {
    return 'mixed-providers'
  }
  if (!flightDetails.flightChangePenalties?.isChangeable) return 'fare-not-changeable'

  return undefined
}

/** Sorts a copy of the given legs by their position in allLegs (booking order). */
export function sortLegsByBookingOrder(
  legs: Array<App.LegWithFlightItemId>,
  allLegs: Array<App.LegWithFlightItemId>,
): Array<App.LegWithFlightItemId> {
  return [...legs].sort((a, b) => {
    const indexA = allLegs.findIndex(leg => leg.id === a.id)
    const indexB = allLegs.findIndex(leg => leg.id === b.id)
    return indexA - indexB
  })
}

/** `YYYY-MM-DD` of a leg departure given as `DD/MM/YYYY-HH:mm` or ISO */
function toLegDepartureDate(departureDateTime: string): string {
  const [datePart] = departureDateTime.split('-')
  const [day, month, year] = datePart.split('/')
  return year ? `${year}-${month}-${day}` : departureDateTime.slice(0, 10)
}

/** `VA-303`, ignoring padding and leading zeros of the flight number */
function toFlightIdentity(carrier: string, flightNumber: string): string {
  return `${carrier.trim()}-${flightNumber.trim().replace(/^0+/, '')}`
}

/** The first flight of a journey key, `VA~  303~ ~~MEL~...`, precedes the first `~~` */
function getApprovedFlightIdentity(journeyKey: string): string | undefined {
  const [carrier, flightNumber] = journeyKey.split('~~')[0].split('~')
  return carrier && flightNumber ? toFlightIdentity(carrier, flightNumber) : undefined
}

function getBookedFlightIdentity(leg: App.Leg): string | undefined {
  const firstFlight = leg.flights[0]
  return firstFlight ? toFlightIdentity(firstFlight.marketingAirline.code, firstFlight.flightNumber) : undefined
}

/**
 * The booked legs the approved flights change, by date or by flight, with the approved departure dates.
 * Mirrors svc-business `getSelectedLegsFromCart`. Empty when the approved flights do not line up with the legs.
 */
export function getApprovedLegsToChange(
  approvedFlights: Array<App.Checkout.FlightItemFlight>,
  allLegs: Array<App.LegWithFlightItemId>,
): Array<App.LegWithFlightItemIdWithNewDepartureDate> {
  if (approvedFlights.length !== allLegs.length) return []

  return allLegs.flatMap((leg, legIndex) => {
    const approvedFlight = approvedFlights[legIndex]
    const isSameDate = approvedFlight.departingDate === toLegDepartureDate(leg.departureDateTime)
    // Compare flights only when both are known
    const approvedIdentity = getApprovedFlightIdentity(approvedFlight.journeyKey)
    const bookedIdentity = getBookedFlightIdentity(leg)
    const isSameFlight = !approvedIdentity || !bookedIdentity || approvedIdentity === bookedIdentity
    return isSameDate && isSameFlight ? [] : [{ ...leg, newDepartureDate: approvedFlight.departingDate }]
  })
}
